import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Collapse,
  TablePagination,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import deathCertificateService from '../services/deathCertificateService';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const DeathCertificate = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // In landscape, width can exceed sm so isMobile is false; short viewport = need scroll/fullscreen behavior
  const isShortViewport = useMediaQuery('(max-height: 600px)');
  const isMobileOrShortViewport = isMobile || isShortViewport;

  // State for certificates list
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [validationStatus, setValidationStatus] = useState('');

  // View details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Verify dialog state
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyData, setVerifyData] = useState({
    validation_status: '',
    admin_remarks: '',
  });
  const [verifyLoading, runVerify] = useActionLock();

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Death Certificate Details popup state
  const [deathCertPopupOpen, setDeathCertPopupOpen] = useState(false);
  const [deathCertPopupData, setDeathCertPopupData] = useState(null);
  const [deathCertPopupForm, setDeathCertPopupForm] = useState({
    admin_remark: '',
    supporting_doc: null,
  });
  const [deathCertPopupLoading, setDeathCertPopupLoading] = useState(false);
  const [validLocked, runMarkValid] = useActionLock();
  const [invalidLocked, runMarkInvalid] = useActionLock();

  // Admin document preview dialog state
  const [adminDocPreviewOpen, setAdminDocPreviewOpen] = useState(false);
  const [adminDocPreviewUrl, setAdminDocPreviewUrl] = useState(null);
  const [adminDocBlobMimeType, setAdminDocBlobMimeType] = useState(null);

  // Certificate blob preview state (authenticated proxy)
  const [certBlobUrl, setCertBlobUrl] = useState(null);
  const [certBlobLoading, setCertBlobLoading] = useState(false);

  // Fetch certificates
  const fetchCertificates = useCallback(async (customPage, customSearch, customStatus) => {
    setLoading(true);
    setError(null);

    const params = {
      page: (customPage !== undefined ? customPage : page) + 1,
      page_size: rowsPerPage,
    };

    const searchValue = customSearch !== undefined ? customSearch : searchQuery;
    const statusValue = customStatus !== undefined ? customStatus : validationStatus;

    if (searchValue) params.search = searchValue;
    if (statusValue) params.validation_status = statusValue;

    const result = await deathCertificateService.getDeathCertificates(params);

    if (result.success) {
      setCertificates(result.data.results || []);
      setTotalCount(result.data.count || 0);
    } else {
      setError(result.error);
      setCertificates([]);
    }

    setLoading(false);
  }, [page, rowsPerPage, searchQuery, validationStatus]);

  // Initial fetch and when page/rowsPerPage changes
  useEffect(() => {
    fetchCertificates();
  }, [page, rowsPerPage]);

  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setPage(0);
      fetchCertificates(0, searchQuery, validationStatus);
    }
  };

  // Handle search button click
  const handleSearchClick = () => {
    setPage(0);
    fetchCertificates(0, searchQuery, validationStatus);
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    const newStatus = e.target.value;
    setValidationStatus(newStatus);
    setPage(0);
    fetchCertificates(0, searchQuery, newStatus);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // View details handlers
  const handleViewDetails = async (certificate) => {
    // Clean up previous blob URL
    if (certBlobUrl) URL.revokeObjectURL(certBlobUrl);
    setCertBlobUrl(null);
    setCertBlobLoading(true);

    setSelectedCertificate(certificate);
    setDetailsLoading(true);
    setDetailsDialogOpen(true);

    // Fetch details and certificate blob in parallel
    const [detailResult, blobResult] = await Promise.all([
      deathCertificateService.getCertificateDetails(certificate.certificate_id),
      deathCertificateService.getCertificateFileBlob(certificate.certificate_id),
    ]);

    setDetailsLoading(false);
    setCertBlobLoading(false);

    if (detailResult.success) {
      setSelectedCertificate(detailResult.data);
    } else {
      setSnackbar({
        open: true,
        message: detailResult.error,
        severity: 'error',
      });
      setDetailsDialogOpen(false);
    }

    if (blobResult.success) {
      setCertBlobUrl(blobResult.data.blobUrl);
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedCertificate(null);
    if (certBlobUrl) URL.revokeObjectURL(certBlobUrl);
    setCertBlobUrl(null);
  };

  // Verify handlers
  const handleVerifyClick = (certificate, status) => {
    setSelectedCertificate(certificate);
    setVerifyData({
      validation_status: status,
      admin_remarks: '',
    });
    setVerifyDialogOpen(true);
  };

  const handleCloseVerifyDialog = () => {
    setVerifyDialogOpen(false);
    setSelectedCertificate(null);
    setVerifyData({
      validation_status: '',
      admin_remarks: '',
    });
  };

  const handleVerifyCertificate = async () => {
    if (!verifyData.validation_status) {
      setSnackbar({
        open: true,
        message: 'Please select a validation status',
        severity: 'error',
      });
      return;
    }

    const result = await deathCertificateService.verifyCertificate(
      selectedCertificate.certificate_id,
      verifyData
    );

    if (result.success) {
      setSnackbar({
        open: true,
        message: `Certificate marked as ${verifyData.validation_status}`,
        severity: 'success',
      });
      handleCloseVerifyDialog();
      fetchCertificates();
    } else {
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return { bgcolor: '#FFF3E0', color: '#E65100' };
      case 'valid':
        return { bgcolor: '#D4F4DD', color: '#2E7D32' };
      case 'invalid':
        return { bgcolor: '#FEE2E2', color: '#DC2626' };
      default:
        return { bgcolor: '#F3F4F6', color: '#6B7280' };
    }
  };

  // Format status text
  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Handle download
  const handleDownload = (url, fileName) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = fileName || 'death-certificate';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Death Certificate Details popup handlers
  const handleUserIdClick = async (certificate) => {
    setDeathCertPopupForm({
      admin_remark: '',
      supporting_doc: null,
    });
    setDeathCertPopupOpen(true);
    setDeathCertPopupLoading(true);

    // Fetch full certificate details
    const result = await deathCertificateService.getCertificateDetails(certificate.certificate_id);

    setDeathCertPopupLoading(false);

    if (result.success) {
      setDeathCertPopupData(result.data);
    } else {
      // Fall back to basic certificate data if fetch fails
      setDeathCertPopupData(certificate);
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
    }
  };

  const handleCloseDeathCertPopup = () => {
    setDeathCertPopupOpen(false);
    setDeathCertPopupData(null);
    setDeathCertPopupForm({
      admin_remark: '',
      supporting_doc: null,
    });
  };

  const handleDeathCertPopupFileChange = (e) => {
    const file = e.target.files[0];
    setDeathCertPopupForm({ ...deathCertPopupForm, supporting_doc: file });
  };

  const handleMarkValidAndNotify = async () => {
    const result = await deathCertificateService.verifyCertificate(
      deathCertPopupData.certificate_id,
      {
        validation_status: 'valid',
        admin_remarks: deathCertPopupForm.admin_remark,
        supporting_doc_file: deathCertPopupForm.supporting_doc,
      }
    );

    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Certificate marked as valid successfully',
        severity: 'success',
      });
      handleCloseDeathCertPopup();
      fetchCertificates();
    } else {
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
    }
  };

  const handleMarkInvalidAndRequestResubmission = async () => {
    if (!deathCertPopupForm.admin_remark) {
      setSnackbar({
        open: true,
        message: 'Please provide admin remarks when marking as invalid',
        severity: 'error',
      });
      return;
    }

    const result = await deathCertificateService.verifyCertificate(
      deathCertPopupData.certificate_id,
      {
        validation_status: 'invalid',
        admin_remarks: deathCertPopupForm.admin_remark,
        supporting_doc_file: deathCertPopupForm.supporting_doc,
      }
    );

    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Certificate marked as invalid. User will be notified to resubmit.',
        severity: 'success',
      });
      handleCloseDeathCertPopup();
      fetchCertificates();
    } else {
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}
          >
            Manage Death Certificate
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              sx={{
                minWidth: { xs: '100%', sm: 180 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover fieldset': { borderColor: 'transparent' },
                  '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton size="small" onClick={handleSearchClick} sx={{ padding: 0 }}>
                      <SearchIcon sx={{ color: '#6B7280' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              value={validationStatus}
              onChange={handleStatusFilterChange}
              sx={{
                minWidth: 130,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& fieldset': { borderColor: 'transparent' },
                },
              }}
              displayEmpty
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="valid">Valid</MenuItem>
              <MenuItem value="invalid">Invalid</MenuItem>
            </TextField>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => fetchCertificates()}
                sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#F8FAFC' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table - Desktop/Tablet */}
      {!loading && !error && !isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid #ECF0F1',
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : certificates.length === 0 ? (
            <Box sx={{ padding: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">No death certificates found</Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                      }}
                    >
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                        Certificate ID
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                        User Name
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                        Uploaded By
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                        Submitted On
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                        Verification Status
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {certificates.map((certificate, index) => {
                      const statusColors = getStatusColor(certificate.validation_status);
                      const isPending = certificate.validation_status?.toLowerCase() === 'pending';
                      return (
                        <TableRow
                          key={certificate.certificate_id}
                          sx={{
                            backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                            '&:hover': { backgroundColor: '#ECF0F1' },
                          }}
                        >
                          <TableCell
                            sx={{
                              color: '#4E83BD',
                              fontSize: '14px',
                              padding: '16px',
                              fontWeight: 600,
                              // cursor: 'pointer',
                              // '&:hover': {
                              //   textDecoration: 'underline',
                              // },
                            }}
                          // onClick={() => handleUserIdClick(certificate)}
                          >
                            {certificate.user_id || certificate.certificate_id || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: '#2C3E50', fontSize: '14px', padding: '16px' }}>

                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {certificate.user_name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#2C3E50', fontSize: '14px', padding: '16px' }}>
                            {certificate.uploaded_by_nominee_name ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {certificate.uploaded_by_nominee_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748B' }}>
                                  Nominee
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Admin
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748B' }}>
                                  Administrator
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: '#2C3E50', fontSize: '14px', padding: '16px' }}>
                            {formatDate(certificate.uploaded_at || certificate.submitted_on)}
                          </TableCell>
                          <TableCell sx={{ padding: '16px' }}>
                            <Chip
                              label={formatStatus(certificate.validation_status)}
                              size="small"
                              sx={{
                                ...statusColors,
                                fontWeight: 600,
                                fontSize: '12px',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ padding: '16px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(certificate)}
                                  sx={{
                                    color: '#4E83BD',
                                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                                    },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleUserIdClick(certificate)}
                                  sx={{
                                    color: '#4E83BD',
                                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  borderTop: '1px solid #ECF0F1',
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    color: '#2C3E50',
                  },
                }}
              />
            </>
          )}
        </Paper>
      )}

      {/* Mobile Card View */}
      {!loading && !error && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {certificates.length === 0 ? (
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #ECF0F1', padding: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">No death certificates found</Typography>
            </Paper>
          ) : (
            certificates.map((certificate, index) => {
              const statusColors = getStatusColor(certificate.validation_status);
              return (
                <Paper
                  key={certificate.certificate_id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid #ECF0F1',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      backgroundColor: '#4E83BD',
                      padding: '10px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Chip
                      label={formatStatus(certificate.validation_status)}
                      size="small"
                      sx={{
                        ...statusColors,
                        fontWeight: 600,
                        fontSize: '11px',
                        height: '22px',
                      }}
                    />
                    <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>
                      {formatDate(certificate.uploaded_at || certificate.submitted_on)}
                    </Typography>
                  </Box>

                  {/* Card Content */}
                  <Box sx={{ padding: 2 }}>
                    <Typography variant="body2" sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                      User
                    </Typography>
                    <Typography sx={{ color: '#2C3E50', fontSize: '14px', fontWeight: 600, marginBottom: 1 }}>
                      {certificate.user_name || 'N/A'}
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1, marginBottom: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                          Uploaded By
                        </Typography>
                        <Typography sx={{ color: '#2C3E50', fontSize: '14px', fontWeight: 500 }}>
                          {certificate.uploaded_by_nominee_name ? (
                            <>
                              {certificate.uploaded_by_nominee_name}
                              <Typography component="span" sx={{ fontSize: '10px', color: '#64748B', ml: 1, fontWeight: 400 }}>
                                (Nominee)
                              </Typography>
                            </>
                          ) : 'Admin'}
                        </Typography>
                      </Box>
                      {!isMobile && (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                            User ID
                          </Typography>
                          <Typography sx={{ color: '#64748B', fontSize: '12px', fontFamily: 'monospace', bgcolor: '#F1F5F9', padding: '3px 6px', borderRadius: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', display: 'block' }}>
                            {certificate.user_id || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                        Certificate ID
                      </Typography>
                      <Typography sx={{ color: '#64748B', fontSize: '12px', fontFamily: 'monospace', bgcolor: '#F1F5F9', padding: '3px 6px', borderRadius: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', display: 'block' }}>
                        {certificate.certificate_id || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Card Actions */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      padding: '8px 16px',
                      borderTop: '1px solid #ECF0F1',
                      backgroundColor: '#F8FAFC',
                    }}
                  >
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(certificate)}
                        sx={{
                          color: '#4E83BD',
                          backgroundColor: 'rgba(78, 131, 189, 0.1)',
                          '&:hover': { backgroundColor: 'rgba(78, 131, 189, 0.2)' },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleUserIdClick(certificate)}
                        sx={{
                          color: '#4E83BD',
                          backgroundColor: 'rgba(78, 131, 189, 0.1)',
                          '&:hover': { backgroundColor: 'rgba(78, 131, 189, 0.2)' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })
          )}

          {/* Pagination for Mobile */}
          {certificates.length > 0 && (
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #ECF0F1', overflow: 'hidden' }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '.MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'center', padding: '8px' },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { color: '#64748B', fontSize: '12px' },
                }}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* View Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobileOrShortViewport}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: isMobileOrShortViewport ? 0 : 3,
            maxHeight: isMobileOrShortViewport ? '100vh' : '90vh',
            margin: isMobileOrShortViewport ? 0 : 2,
            height: isMobileOrShortViewport ? '100vh' : 'auto',
            paddingTop: isMobileOrShortViewport ? 'env(safe-area-inset-top, 0px)' : 0,
          }
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            paddingX: { xs: '16px', sm: '24px' },
            paddingTop: { xs: 'max(48px, calc(env(safe-area-inset-top, 0px) + 16px))', sm: '20px' },
            paddingBottom: { xs: '20px', sm: '20px' },
            borderBottom: '1px solid #E5E7EB',
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'white',
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            Certificate Details
          </Typography>
          <IconButton onClick={handleCloseDetailsDialog} sx={{ color: 'white', flexShrink: 0 }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0, overflow: isMobileOrShortViewport ? 'auto' : 'hidden', height: isMobileOrShortViewport ? 'calc(100vh - 110px)' : '75vh', WebkitOverflowScrolling: 'touch' }}>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 6, minHeight: 400 }}>
              <CircularProgress size={48} />
            </Box>
          ) : selectedCertificate ? (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: '100%' }, minWidth: 0, overflow: { xs: 'visible', md: 'hidden' } }}>
              {/* Left Side - PDF/Image Preview */}
              <Box
                sx={{
                  width: { xs: '100%', md: '55%' },
                  bgcolor: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xs: 'auto', md: '100%' },
                  minHeight: { xs: 0, md: 0 },
                  maxHeight: { xs: 300, md: '100%' },
                  minWidth: 0,
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                {/* Preview Header */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: { xs: '10px 16px', md: '12px 20px' },
                    borderBottom: '1px solid #E5E7EB',
                    bgcolor: '#ffffff',
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: 'black', fontSize: '14px' }}>
                    Certificate Preview
                  </Typography>
                  {certBlobUrl && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(
                        certBlobUrl,
                        selectedCertificate.file_name || selectedCertificate.original_filename || 'death-certificate'
                      )}
                      sx={{
                        bgcolor: '#4E83BD',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '12px',
                        '&:hover': { bgcolor: '#3A6A99' },
                      }}
                    >
                      Download
                    </Button>
                  )}
                </Box>

                {/* Preview Content */}
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: '#F5F5F5', maxHeight: { xs: 250, md: 'none' } }}>
                  {(() => {
                    const certificatePath = selectedCertificate.certificate_file_path || selectedCertificate.original_filename || selectedCertificate.certificate_url || '';
                    const fileName = certificatePath.split('/').pop() || 'certificate';
                    const fileType = selectedCertificate.certificate_file_type || '';

                    const lowerPath = certificatePath.toLowerCase();
                    const isPdf = fileType.includes('pdf') || lowerPath.includes('.pdf');
                    const isImage =
                      fileType.includes('image') ||
                      /\.(jpg|jpeg|png|gif|webp)$/i.test(certificatePath);
                    const isDoc =
                      fileType.includes('word') ||
                      lowerPath.endsWith('.doc') ||
                      lowerPath.endsWith('.docx');

                    // Show loading while fetching certificate blob
                    if (certBlobLoading) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            padding: 4,
                          }}
                        >
                          <CircularProgress size={40} sx={{ color: '#4E83BD' }} />
                          <Typography sx={{ fontWeight: 500, color: '#64748B', textAlign: 'center' }}>
                            Loading certificate...
                          </Typography>
                        </Box>
                      );
                    }

                    if (!certBlobUrl) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            padding: 4,
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 48, color: '#94A3B8' }} />
                          <Typography sx={{ fontWeight: 500, color: '#64748B', textAlign: 'center' }}>
                            No certificate file uploaded
                          </Typography>
                        </Box>
                      );
                    }

                    // Inline preview for PDF files
                    if (isPdf) {
                      // Mobile browsers don't support inline PDF viewing via iframe
                      if (isMobile) {
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 2,
                              padding: 4,
                              textAlign: 'center',
                              height: '100%',
                            }}
                          >
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: 2,
                                bgcolor: '#4E83BD',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 700,
                              }}
                            >
                              PDF
                            </Box>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '14px' }}>
                              {fileName}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 1 }}>
                              PDF preview is not supported on mobile browsers
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<VisibilityIcon />}
                              onClick={() => window.open(certBlobUrl, '_blank')}
                              sx={{
                                bgcolor: '#4E83BD',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { bgcolor: '#3A6A99' },
                              }}
                            >
                              Open PDF
                            </Button>
                          </Box>
                        );
                      }

                      // Desktop: use iframe for PDF preview
                      return (
                        <Box sx={{ width: '100%', height: '100%' }}>
                          <iframe
                            src={certBlobUrl}
                            title={fileName}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                          />
                        </Box>
                      );
                    }

                    // Inline preview for images
                    if (isImage) {
                      return (
                        <Box
                          sx={{
                            width: '100%',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            maxHeight: { xs: 220, md: 'none' },
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            component="img"
                            src={certBlobUrl}
                            alt={fileName}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: { xs: 200, md: '100%' },
                              objectFit: 'contain',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              display: 'block',
                            }}
                          />
                        </Box>
                      );
                    }

                    // Fallback: show file info and open button (DOC/DOCX/other files)
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 2,
                          padding: 4,
                          textAlign: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            bgcolor: isPdf ? '#DC2626' : isDoc ? '#2563EB' : '#4E83BD',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 700,
                          }}
                        >
                          {isPdf ? 'PDF' : isDoc ? 'DOCX' : isImage ? 'IMG' : 'FILE'}
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '14px' }}>
                          {fileName}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#64748B' }}>
                          {fileType || 'Unknown type'} • {selectedCertificate.certificate_file_size_kb ? `${selectedCertificate.certificate_file_size_kb} KB` : 'Unknown size'}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<VisibilityIcon />}
                          onClick={() => window.open(certBlobUrl, '_blank')}
                          sx={{
                            mt: 1,
                            bgcolor: '#4E83BD',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#3A6A99' },
                          }}
                        >
                          Open Certificate in New Tab
                        </Button>
                        <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 1 }}>
                          Preview not available. Click above to view the file directly.
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

              {/* Right Side - Details */}
              <Box
                sx={{
                  width: { xs: '100%', md: '45%' },
                  bgcolor: '#F8FAFC',
                  overflow: { xs: 'visible', md: 'auto' },
                  padding: { xs: 2, md: 3 },
                  paddingBottom: { xs: 4, md: 3 },
                  height: { xs: 'auto', md: '100%' },
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                {/* Deceased User Information */}
                <Paper
                  elevation={0}
                  sx={{
                    padding: { xs: 2, md: 2.5 },
                    marginBottom: 2,
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    bgcolor: 'white',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                    <Box sx={{ width: 4, height: 20, bgcolor: '#4E83BD', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                      Deceased User Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Name</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                        {selectedCertificate.user_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Email</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {selectedCertificate.user_email || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>User ID</Typography>
                      <Typography sx={{ fontSize: '13px', color: '#64748B', fontFamily: 'monospace', bgcolor: '#F1F5F9', padding: '4px 8px', borderRadius: 1, wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                        {selectedCertificate.user_id || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Nominee Information */}
                <Paper
                  elevation={0}
                  sx={{
                    padding: { xs: 2, md: 2.5 },
                    marginBottom: 2,
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    bgcolor: 'white',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                    <Box sx={{ width: 4, height: 20, bgcolor: '#10B981', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                      Uploader Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Uploader</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                        {selectedCertificate.uploaded_by_nominee_name || 'Admin'}
                      </Typography>
                    </Grid>
                    {selectedCertificate.uploaded_by_nominee_name && (
                      <>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Relationship</Typography>
                          <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                            {selectedCertificate.uploaded_by_nominee_relationship || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Email</Typography>
                          <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {selectedCertificate.uploaded_by_nominee_email || 'N/A'}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>

                {/* Certificate Information */}
                <Paper
                  elevation={0}
                  sx={{
                    padding: { xs: 2, md: 2.5 },
                    marginBottom: 2,
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    bgcolor: 'white',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                    <Box sx={{ width: 4, height: 20, bgcolor: '#F59E0B', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                      Certificate Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Certificate ID</Typography>
                      <Typography sx={{ fontSize: '13px', color: '#64748B', fontFamily: 'monospace', bgcolor: '#F1F5F9', padding: '4px 8px', borderRadius: 1, wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                        {selectedCertificate.certificate_id || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Submitted On</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                        {formatDate(selectedCertificate.uploaded_at || selectedCertificate.submitted_on)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Status</Typography>
                      <Chip
                        label={formatStatus(selectedCertificate.validation_status)}
                        size="small"
                        sx={{
                          ...getStatusColor(selectedCertificate.validation_status),
                          fontWeight: 600,
                          fontSize: '12px',
                        }}
                      />
                    </Grid>
                    {selectedCertificate.validated_by && (
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Validated By</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                          {selectedCertificate.validated_by}
                        </Typography>
                      </Grid>
                    )}
                    {selectedCertificate.validated_at && (
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Validated At</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                          {formatDate(selectedCertificate.validated_at)}
                        </Typography>
                      </Grid>
                    )}
                    {selectedCertificate.admin_remarks && (
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 0.5 }}>Admin Remarks</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500, bgcolor: '#FFF3E0', padding: '8px 12px', borderRadius: 1 }}>
                          {selectedCertificate.admin_remarks}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {/* Admin Supporting Document */}
                {(selectedCertificate.admin_supporting_doc_url || selectedCertificate.supporting_doc_url || selectedCertificate.admin_document_url) && (
                  <Paper
                    elevation={0}
                    sx={{
                      padding: { xs: 2, md: 2.5 },
                      marginBottom: 2,
                      borderRadius: 2,
                      border: '1px solid #E5E7EB',
                      bgcolor: 'white',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                      <Box sx={{ width: 4, height: 20, bgcolor: '#3f70a1', borderRadius: 1 }} />
                      <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                        Admin Supporting Document
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 1 }}>
                          Document uploaded by admin during validation
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={async () => {
                            if (selectedCertificate.certificate_id) {
                              const blobResult = await deathCertificateService.getAdminSupportingDocBlob(selectedCertificate.certificate_id);
                              if (blobResult.success) {
                                setAdminDocPreviewUrl(blobResult.data.blobUrl);
                                setAdminDocBlobMimeType(blobResult.data.mimeType);
                                setAdminDocPreviewOpen(true);
                              }
                            }
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            color: '#3f70a1',
                            borderColor: '#3f70a1',
                            '&:hover': {
                              borderColor: '#2d5a87',
                              backgroundColor: '#EBF5FF',
                            },
                          }}
                        >
                          View Admin Document
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* Validation History */}
                {selectedCertificate.validation_history && selectedCertificate.validation_history.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      padding: { xs: 2, md: 2.5 },
                      borderRadius: 2,
                      border: '1px solid #E5E7EB',
                      bgcolor: 'white',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                      <Box sx={{ width: 4, height: 20, bgcolor: '#8B5CF6', borderRadius: 1 }} />
                      <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                        Validation History
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#64748B' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#64748B' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#64748B' }}>By</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#64748B' }}>Remarks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedCertificate.validation_history.map((history, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontSize: '13px' }}>{formatDate(history.created_at || history.date)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={formatStatus(history.status)}
                                  size="small"
                                  sx={{
                                    ...getStatusColor(history.status),
                                    fontWeight: 600,
                                    fontSize: '11px',
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: '13px' }}>{history.validated_by || 'N/A'}</TableCell>
                              <TableCell sx={{ fontSize: '13px' }}>{history.remarks || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ padding: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">No details available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog
        open={verifyDialogOpen}
        onClose={handleCloseVerifyDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
      >
        <DialogContent sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2C3E50' }}>
              Verify Certificate
            </Typography>
            <IconButton onClick={handleCloseVerifyDialog} sx={{ color: '#2C3E50' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ marginBottom: 2.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2C3E50', marginBottom: 1 }}>
              Certificate ID
            </Typography>
            <TextField
              fullWidth
              value={selectedCertificate?.certificate_id || ''}
              disabled
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#F8FAFC',
                },
              }}
            />
          </Box>

          <Box sx={{ marginBottom: 2.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2C3E50', marginBottom: 1 }}>
              Validation Status
            </Typography>
            <TextField
              select
              fullWidth
              value={verifyData.validation_status}
              onChange={(e) => setVerifyData({ ...verifyData, validation_status: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            >
              <MenuItem value="valid">Valid</MenuItem>
              <MenuItem value="invalid">Invalid</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ marginBottom: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2C3E50', marginBottom: 1 }}>
              Admin Remarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={verifyData.admin_remarks}
              onChange={(e) => setVerifyData({ ...verifyData, admin_remarks: e.target.value })}
              placeholder="Enter remarks..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleCloseVerifyDialog}
              disabled={verifyLoading}
              sx={{
                textTransform: 'none',
                color: '#2C3E50',
                borderColor: '#D1D9E0',
                paddingX: 3,
              }}
            >
              Cancel
            </Button>
            <ActionButton
              variant="contained"
              loading={verifyLoading}
              onClick={() => runVerify(handleVerifyCertificate)}
              loadingText={`Marking as ${formatStatus(verifyData.validation_status)}...`}
              sx={{
                textTransform: 'none',
                backgroundColor: verifyData.validation_status === 'valid' ? '#2E7D32' : '#DC2626',
                paddingX: 3,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: verifyData.validation_status === 'valid' ? '#047857' : '#B91C1C',
                },
              }}
            >
              {`Mark as ${formatStatus(verifyData.validation_status)}`}
            </ActionButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Death Certificate Details Popup */}
      <Dialog
        open={deathCertPopupOpen}
        onClose={handleCloseDeathCertPopup}
        maxWidth="md"
        fullWidth
        fullScreen={isMobileOrShortViewport}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: isMobileOrShortViewport ? 0 : 3,
            margin: isMobileOrShortViewport ? 0 : 2,
            height: isMobileOrShortViewport ? '100vh' : 'auto',
            maxHeight: isMobileOrShortViewport ? '100vh' : '90vh',
            paddingTop: isMobileOrShortViewport ? 'env(safe-area-inset-top, 0px)' : 0,
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            paddingX: { xs: '16px', sm: '24px' },
            paddingTop: { xs: 'max(48px, calc(env(safe-area-inset-top, 0px) + 16px))', sm: '20px' },
            paddingBottom: { xs: '20px', sm: '20px' },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '18px' }}>
            Death Certificate Details
          </Typography>
          <IconButton
            onClick={handleCloseDeathCertPopup}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0, overflow: 'auto', height: isMobileOrShortViewport ? 'calc(100vh - 110px)' : 'auto', WebkitOverflowScrolling: 'touch' }}>
          {deathCertPopupLoading && !deathCertPopupData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 6, minHeight: 300 }}>
              <CircularProgress size={48} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minWidth: 0, overflow: 'hidden' }}>
              {/* Left Section - Certificate Info */}
              <Box sx={{
                flex: 1,
                padding: 3,
                borderRight: { md: '1px solid #E5E7EB' },
                borderBottom: { xs: '1px solid #E5E7EB', md: 'none' },
                minWidth: 0,
                overflow: 'hidden',
              }}>
                {/* User Info Card */}
                <Paper
                  elevation={0}
                  sx={{
                    padding: 2.5,
                    marginBottom: 2.5,
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FAFBFC',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                    <Box sx={{ width: 4, height: 20, bgcolor: '#4E83BD', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                      User Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                        User ID
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#64748B', fontFamily: 'monospace', bgcolor: '#F1F5F9', padding: '4px 8px', borderRadius: 1, wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                        {deathCertPopupData?.user_id || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                        User Name
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                        {deathCertPopupData?.user_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                        User Email
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {deathCertPopupData?.user_email || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Certificate Info Card */}
                <Paper
                  elevation={0}
                  sx={{
                    padding: 2.5,
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FAFBFC',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                    <Box sx={{ width: 4, height: 20, bgcolor: '#10B981', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                      Certificate Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                        Submission Date
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>
                        {formatDate(deathCertPopupData?.uploaded_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                        Status
                      </Typography>
                      <Chip
                        label={formatStatus(deathCertPopupData?.validation_status)}
                        size="small"
                        sx={{
                          ...getStatusColor(deathCertPopupData?.validation_status),
                          fontWeight: 600,
                          fontSize: '12px',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 1 }}>
                        Certificate File
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={async () => {
                          if (deathCertPopupData?.certificate_id) {
                            const blobResult = await deathCertificateService.getCertificateFileBlob(deathCertPopupData.certificate_id);
                            if (blobResult.success) {
                              window.open(blobResult.data.blobUrl, '_blank');
                            }
                          }
                        }}
                        disabled={!deathCertPopupData?.certificate_id}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '13px',
                          color: '#4E83BD',
                          borderColor: '#4E83BD',
                          '&:hover': {
                            borderColor: '#3A6A99',
                            backgroundColor: '#EBF5FF',
                          },
                        }}
                      >
                        View Certificate
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Right Section - Admin Actions */}
              <Box sx={{
                flex: 1,
                padding: 3,
                backgroundColor: '#FFFFFF',
                minWidth: 0,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: '#F59E0B', borderRadius: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                    Admin Actions
                  </Typography>
                </Box>

                {/* Admin Remark */}
                <Box sx={{ marginBottom: 2.5 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Admin Remark
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Enter your remarks here..."
                    value={deathCertPopupForm.admin_remark}
                    onChange={(e) => setDeathCertPopupForm({ ...deathCertPopupForm, admin_remark: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        '& fieldset': { borderColor: '#E5E7EB' },
                        '&:hover fieldset': { borderColor: '#4E83BD' },
                        '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                      },
                    }}
                  />
                </Box>

                {/* Supporting Doc Upload */}
                <Box sx={{ marginBottom: 3 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Supporting Document (Optional)
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px dashed #D1D9E0',
                      borderRadius: '10px',
                      backgroundColor: '#F8FAFC',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#4E83BD',
                        backgroundColor: '#EBF5FF',
                      },
                    }}
                    component="label"
                  >
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleDeathCertPopupFileChange}
                    />
                    <DownloadIcon sx={{ fontSize: 32, color: '#94A3B8', marginBottom: 1, transform: 'rotate(180deg)' }} />
                    <Typography sx={{ fontSize: '13px', color: '#64748B', textAlign: 'center' }}>
                      {deathCertPopupForm.supporting_doc
                        ? deathCertPopupForm.supporting_doc.name
                        : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#94A3B8', marginTop: 0.5 }}>
                      PDF, JPG, PNG (Max 10MB)
                    </Typography>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, paddingBottom: { xs: 4, md: 0 } }}>
                  <ActionButton
                    variant="contained"
                    fullWidth
                    loading={validLocked}
                    onClick={() => runMarkValid(handleMarkValidAndNotify)}
                    loadingText="Marking valid..."
                    sx={{
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #10B981 0%, #2E7D32 100%)',
                      fontWeight: 600,
                      padding: '14px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2E7D32 0%, #047857 100%)',
                        boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                      },
                    }}
                  >
                    Mark Valid & Notify
                  </ActionButton>
                  <ActionButton
                    variant="contained"
                    fullWidth
                    loading={invalidLocked}
                    onClick={() => runMarkInvalid(handleMarkInvalidAndRequestResubmission)}
                    loadingText="Marking invalid..."
                    sx={{
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                      fontWeight: 600,
                      padding: '14px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                        boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                      },
                    }}
                  >
                    Mark Invalid & Request Resubmission
                  </ActionButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Document Preview Dialog */}
      <Dialog
        open={adminDocPreviewOpen}
        onClose={() => {
          setAdminDocPreviewOpen(false);
          if (adminDocPreviewUrl) URL.revokeObjectURL(adminDocPreviewUrl);
          setAdminDocPreviewUrl(null);
          setAdminDocBlobMimeType(null);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            margin: isMobile ? 0 : 2,
            height: isMobile ? '100vh' : '80vh',
            maxHeight: isMobile ? '100vh' : '80vh',
          }
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: '1px solid #E5E7EB',
            background: 'linear-gradient(135deg, #3f70a1 0%, #2d5a87 100%)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '16px' }}>
            Admin Supporting Document
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => {
                if (adminDocPreviewUrl) {
                  const link = document.createElement('a');
                  link.href = adminDocPreviewUrl;
                  link.download = 'admin-supporting-document';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '12px',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              Download
            </Button>
            <IconButton
              onClick={() => {
                setAdminDocPreviewOpen(false);
                if (adminDocPreviewUrl) URL.revokeObjectURL(adminDocPreviewUrl);
                setAdminDocPreviewUrl(null);
                setAdminDocBlobMimeType(null);
              }}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F5F5F5', overflow: 'auto' }}>
          {adminDocPreviewUrl && (() => {
            const isPdf = adminDocBlobMimeType?.includes('pdf') || false;
            const isImage = adminDocBlobMimeType?.startsWith('image/') || false;

            if (isPdf) {
              // Mobile browsers don't support inline PDF viewing via iframe
              if (isMobile) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      padding: 4,
                      textAlign: 'center',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        bgcolor: '#4E83BD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 700,
                      }}
                    >
                      PDF
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '14px' }}>
                      Admin Supporting Document
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#64748B', marginBottom: 1 }}>
                      PDF preview is not supported on mobile browsers
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<VisibilityIcon />}
                      onClick={() => window.open(adminDocPreviewUrl, '_blank')}
                      sx={{
                        bgcolor: '#4E83BD',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#3A6A99' },
                      }}
                    >
                      Open PDF
                    </Button>
                  </Box>
                );
              }

              // Desktop: use iframe for PDF preview
              return (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <iframe
                    src={adminDocPreviewUrl}
                    title="Admin Document Preview"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </Box>
              );
            }

            if (isImage) {
              return (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 3,
                  }}
                >
                  <img
                    src={adminDocPreviewUrl}
                    alt="Admin Supporting Document"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  />
                </Box>
              );
            }

            // Fallback for other file types
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 4,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    bgcolor: '#3f70a1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 700,
                    marginBottom: 2,
                  }}
                >
                  FILE
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '14px', marginBottom: 1 }}>
                  Preview not available for this file type
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={() => window.open(adminDocPreviewUrl, '_blank')}
                  sx={{
                    mt: 2,
                    bgcolor: '#3f70a1',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#2d5a87' },
                  }}
                >
                  Open in New Tab
                </Button>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeathCertificate;
